import Big from 'big.js';
import {ReplaySubject, combineLatest} from 'rxjs';
import {map, throttleTime} from 'rxjs/operators';
import {div, gt, lte, max, min, minus, numUtil, plus, pow10, times, toFixed, toString} from '@/helper/num';
import {calcValueBalanceUtils} from '@/helper/calc-value-balance';
import {marginRiskLevels$, currenciesCache$} from '@/config';
import {calcMoneyData$} from '@/calc/fiat';
import {DEFAULT_USD_PRICE_SCALE} from '@/const/data-config';
import {indexPricesCache$} from '@/price-index/price-index';
import {allSubWalletsCache$} from '@/spot-wallets/sub-users-all/sub-users-assets';
import {isUtaSpot$} from '@/user/user';
import {marginAccountsCache$, innerCurrenciesCache$} from './accounts-cache';
import {marginTradeCrossRiskLevel$} from './risk-level';
import type {
  ws,
  MarginAccountsData,
  PriceEntity,
  CalcMoneyData,
  MarginAccountRiskLevel,
  MarginCurrencyEntity,
  MarginRiskLevels,
  MarginAccountBase,
  MarginAccount,
  CurrencyEntity,
} from '@/types';

type MarginAccountRaw = ws.MarginAccountRaw;

export const marginAccountsData$ = new ReplaySubject<MarginAccountsData>(1);
export const marginAccountOverview$ = new ReplaySubject<OverviewData>(1);

export const HourlyInterestRatePrecision = 6;
export const DailyInterestRatePrecision = 4;
export const AnnualInterestRatePrecision = 2;

combineLatest([
  isUtaSpot$,
  marginAccountsCache$,
  marginTradeCrossRiskLevel$,
  innerCurrenciesCache$,
  marginRiskLevels$,
  indexPricesCache$,
  calcMoneyData$,
  currenciesCache$,
  allSubWalletsCache$,
])
  .pipe(throttleTime(100), map(convertAccounts), map(convertAccountRelyOnTotalAsset))
  .subscribe(({accounts, overview}) => {
    marginAccountsData$.next({accounts});
    marginAccountOverview$.next(overview);
  });

function convertAccounts([
  isUtaSpot,
  cache,
  marginTradeCrossRiskLevel,
  currencies,
  marginRiskLevels,
  indexPricesCache,
  calcMoneyData,
  currenciesCache,
  allSubWalletsCache,
]: [
  boolean,
  Map<string, MarginAccountRaw>,
  MarginAccountRiskLevel,
  Map<string, MarginCurrencyEntity>,
  MarginRiskLevels,
  Map<string, PriceEntity>,
  CalcMoneyData,
  Map<string, CurrencyEntity>,
  Map<string, number>
]): [InnerMarginAccount[], MarginRiskLevels] {
  const marginAccount = cache
    .toArray((account: MarginAccountRaw) =>
      convertAccount(
        account,
        marginTradeCrossRiskLevel,
        currencies,
        indexPricesCache,
        calcMoneyData,
        currenciesCache,
        // 未升级用户 子账号资产统计在spot account 账户中
        isUtaSpot ? allSubWalletsCache : new Map()
      )
    )
    .filter(Boolean)
    .sort((a, b) => (gt(b.totalBalanceValue, a.totalBalanceValue) ? 1 : -1));
  return [marginAccount, marginRiskLevels];
}
interface InnerMarginAccount extends MarginAccountBase {
  /**
   * 当前资产剩余可借金额(有正有负) = （（正资产 - 负债） * (当前资产最大杠杆 - 1)* 借贷系数 - 负债）* index price
   *
   * remainingBorrow = (AssetBalance - DebtBalance) * (MaxLeverage - 1) * coefficient - DebtBalance
   */
  remainingBorrowValueEv: string; // 当前抵押品允许的最大杠杆，资产剩余可借金额 (有正有负)，其他资产不参与计算。remainingBorrowEv * IndexPrice
  remainingBorrow2XValueEv: string; // 当前抵押品允许的 2X 杠杆，资产剩余可借金额 (有正有负)，其他资产不参与计算。remainingBorrowEv * IndexPrice
  valuePrecision: number;
  pricePrecision: number;
  canBorrow: boolean;
  totalBalanceUsdt: string;
  totalBalanceBtc: string;
  tradingBot: number;
  tradingBotStr: string;
}

function convertAccount(
  {
    lastUpdateTimeNs,
    currency,
    balanceEv,
    lockedTradingBalanceEv: lockedBalanceEv,
    borrowedEv,
    cumInterestEv,
    hourlyInterestRateEr,
    maxAmountToBorrowEv,
  }: MarginAccountRaw,
  marginTradeCrossRiskLevel: MarginAccountRiskLevel,
  marginCurrenciesCache: Map<string, MarginCurrencyEntity>,
  indexPricesCache: Map<string, PriceEntity>,
  calcMoneyData: CalcMoneyData,
  currenciesCache: Map<string, CurrencyEntity>,
  allSubWalletsCache: Map<string, number>
): InnerMarginAccount {
  const currencyInfo = marginCurrenciesCache.get(currency);
  const usdCurrency = currenciesCache.get('USD');
  const btcCurrency = currenciesCache.get('BTC');

  const indexPrice = indexPricesCache.get('.' + currency);
  const usdtRate = indexPricesCache.get('.USDT');
  const btcRate = indexPricesCache.get('.BTC');
  if (
    !currencyInfo ||
    !indexPrice ||
    indexPrice.priceEp === 0 ||
    !btcCurrency ||
    !usdCurrency ||
    !usdtRate ||
    !btcRate
  ) {
    // TODO: 当相关价格没有拿到时，不推送相关account
    return null;
  }

  const {
    valuePrecision,
    pricePrecision,
    valueScale,
    name,
    displayCurrency,
    maxAmountToBorrow,
    canBorrow,
    leverage,
    coefficient,
  } = currencyInfo;

  const newMaxAmountToBorrowEv = gt(maxAmountToBorrowEv, 0) ? maxAmountToBorrowEv : maxAmountToBorrow;

  const tradingBot = allSubWalletsCache.has(currency) ? allSubWalletsCache.get(currency) : 0;
  const tradingBotStr = numUtil.delimit(numUtil.floor(tradingBot, valuePrecision, valueScale));

  const lockedTradingBalanceEv = plus(lockedBalanceEv, tradingBot);
  const freezeBalanceEv = lockedTradingBalanceEv;
  const totalBalanceEv = plus(balanceEv, tradingBot);
  const availableBalanceEv = minus(totalBalanceEv, lockedTradingBalanceEv);
  const availableBalance = calcBalance(availableBalanceEv, valuePrecision);
  const availableBalance2 = calcBalance(availableBalanceEv, 2);
  const availableBalance4 = calcBalance(availableBalanceEv, 4);
  const [availableBalanceMoneyWU] = calcMoneyData(displayCurrency, availableBalanceEv, true);
  const [freezeBalanceMoneyWU] = calcMoneyData(displayCurrency, freezeBalanceEv, true);
  const [totalBalanceMoneyWU, totalBalanceValue] = calcMoneyData(displayCurrency, totalBalanceEv, true);

  const totalDebtEv = plus(borrowedEv, cumInterestEv);
  const [totalDebtMoneyWU, totalDebtValue] = calcMoneyData(displayCurrency, totalDebtEv, true);
  const netEquityEv = minus(totalBalanceEv, totalDebtEv);
  const [netEquityMoneyWU, netEquityValue] = calcMoneyData(displayCurrency, netEquityEv, true);
  const dailyInterestRateEr = times(hourlyInterestRateEr, 24);
  const annualInterestRateEr = times(dailyInterestRateEr, 365);

  const indexPriceEp = div(indexPrice.priceEp, pow10(DEFAULT_USD_PRICE_SCALE));
  const netEquityIsGtZero = gt(netEquityEv, 0);
  // 币种不可借贷，remainingBorrowValueEv = 0
  const remainingBorrowValueEv = canBorrow
    ? toString(
        Big(netEquityEv)
          .times(minus(leverage, 1))
          .times(netEquityIsGtZero ? coefficient : 1)
          .minus(totalDebtEv)
          .times(indexPriceEp)
      )
    : '0';
  const remainingBorrow2XValueEv = canBorrow
    ? toString(
        Big(netEquityEv)
          .times(netEquityIsGtZero ? coefficient : 1)
          .minus(totalDebtEv)
          .times(indexPriceEp)
      )
    : '0';
  // 币种不可借贷，remainingMaxAmountToBorrowEv = 0
  const remainingMaxAmountToBorrowEv = canBorrow ? minus(newMaxAmountToBorrowEv, totalDebtEv) : '0';

  const {valueFactor: usdValueFactor, valuePrecision: usdValuePrecision} = usdCurrency;
  const {valuePrecision: btcValuePrecision} = btcCurrency;

  const totalBalanceUsdEv = convertBalanceToUsdEv(totalBalanceEv, currency, indexPricesCache);
  const totalBalanceUsdtEv = Big(totalBalanceUsdEv).times(usdValueFactor).div(usdtRate.priceEp);
  const totalBalanceBtcEv = Big(totalBalanceUsdEv).times(usdValueFactor).div(btcRate.priceEp);

  return {
    time: Math.floor(lastUpdateTimeNs / 1e6),
    currency,
    displayCurrency,
    name,
    totalBalanceEv,
    totalBalance: calcBalance(totalBalanceEv, valuePrecision),
    totalBalanceMoneyWU,
    totalBalanceValue,
    availableBalanceEv,
    availableBalance,
    availableBalanceWU: availableBalance + ' ' + currency,
    availableBalance2,
    availableBalance2WU: availableBalance2 + ' ' + currency,
    availableBalance4,
    availableBalance4WU: availableBalance4 + ' ' + currency,
    availableBalanceMoneyWU,
    freezeBalanceEv,
    freezeBalance: calcBalance(freezeBalanceEv, valuePrecision),
    freezeBalanceMoneyWU,
    lockedTradingBalanceEv,
    lockedTradingBalance: calcBalance(lockedTradingBalanceEv, valuePrecision),
    borrowedEv,
    borrowed: calcBalanceCeil(borrowedEv, valuePrecision),
    cumInterestEv,
    cumInterest: calcBalanceCeil(cumInterestEv, valuePrecision),
    totalDebtEv,
    totalDebt: calcBalanceCeil(totalDebtEv, valuePrecision),
    totalDebtValue,
    totalDebtMoneyWU,
    netEquityEv,
    netEquity: calcBalance(netEquityEv, valuePrecision),
    netEquityValue,
    netEquityMoneyWU,
    maxAmountToBorrowEv: newMaxAmountToBorrowEv,
    maxAmountToBorrow: calcBalance(newMaxAmountToBorrowEv, valuePrecision),
    remainingMaxAmountToBorrowEv,
    remainingMaxAmountToBorrow: calcBalance(remainingMaxAmountToBorrowEv, valuePrecision),
    hourlyInterestRateEr,
    hourlyInterestRate: toFixed(times(hourlyInterestRateEr, 100), HourlyInterestRatePrecision, 3) + '%',
    dailyInterestRateEr,
    dailyInterestRate: toFixed(times(dailyInterestRateEr, 100), DailyInterestRatePrecision, 3) + '%',
    annualInterestRateEr,
    annualInterestRate: toFixed(times(annualInterestRateEr, 100), AnnualInterestRatePrecision, 3) + '%',
    indexPriceEp,
    remainingBorrowValueEv,
    remainingBorrow2XValueEv,
    valuePrecision,
    pricePrecision,
    canBorrow,
    ...marginTradeCrossRiskLevel,
    totalBalanceUsdt: numUtil.delimit(numUtil.floor(totalBalanceUsdtEv, usdValuePrecision)),
    totalBalanceBtc: numUtil.delimit(numUtil.floor(totalBalanceBtcEv, btcValuePrecision)),
    tradingBot,
    tradingBotStr,
  };
}

function calcBalance(balanceEv, valuePrecision): string {
  return numUtil.delimit(toFixed(balanceEv, valuePrecision, 0));
}

function calcBalanceCeil(balanceEv, valuePrecision): string {
  return numUtil.delimit(toFixed(balanceEv, valuePrecision, 3));
}

interface OverviewData {
  netEquityEv: string;
  totalEquityEv: string;
  totalDebtEv: string;
  maxTransferOutValueEv: string;
  totalAvailableEv: string;
  totalMarginEv: string;
  totalFreezeEv: string;
}

function convertAccountRelyOnTotalAsset([accounts, marginRiskLevels]: [InnerMarginAccount[], MarginRiskLevels]): {
  overview: OverviewData;
  accounts: MarginAccount[];
} {
  if (accounts.length < 1) {
    return {
      overview: {
        netEquityEv: '0',
        totalEquityEv: '0',
        totalDebtEv: '0',
        maxTransferOutValueEv: '0',
        totalAvailableEv: '0',
        totalMarginEv: '0',
        totalFreezeEv: '0',
      },
      accounts: [],
    };
  }
  const {
    totalEquityEv,
    totalDebtEv,
    totalEquityCanBorrowEv,
    totalDebtCanBorrowEv,
    netEquityEv,
    remainingBorrowTotalValueEv,
    remainingBorrow2XTotalValueEv,
    totalAvailableEv,
    totalMarginEv,
    totalFreezeEv,
  } = accounts.reduce(
    (
      {
        totalEquityEv,
        totalEquityCanBorrowEv,
        totalDebtEv,
        totalDebtCanBorrowEv,
        netEquityEv,
        remainingBorrowTotalValueEv,
        remainingBorrow2XTotalValueEv,
        totalAvailableEv,
        totalMarginEv,
        totalFreezeEv,
      },
      account
    ) => {
      const curTotalBalanceEv = times(account.totalBalanceEv, account.indexPriceEp);
      const curTotalDebtEv = times(account.totalDebtEv, account.indexPriceEp);
      // 币种不可借贷，不参与最大可转出、最大可借、强平价计算
      return {
        totalEquityEv: plus(totalEquityEv, curTotalBalanceEv),
        totalEquityCanBorrowEv: account.canBorrow
          ? plus(totalEquityCanBorrowEv, curTotalBalanceEv)
          : totalEquityCanBorrowEv,
        totalDebtEv: plus(totalDebtEv, curTotalDebtEv),
        totalDebtCanBorrowEv: account.canBorrow ? plus(totalDebtCanBorrowEv, curTotalDebtEv) : totalDebtCanBorrowEv,
        netEquityEv: plus(netEquityEv, times(account.netEquityEv, account.indexPriceEp)),
        remainingBorrowTotalValueEv: plus(remainingBorrowTotalValueEv, account.remainingBorrowValueEv),
        remainingBorrow2XTotalValueEv: plus(remainingBorrow2XTotalValueEv, account.remainingBorrow2XValueEv),
        totalAvailableEv: plus(totalAvailableEv, times(account.availableBalanceEv, account.indexPriceEp)),
        totalMarginEv: account.canBorrow ? plus(totalMarginEv, curTotalBalanceEv) : totalMarginEv,
        totalFreezeEv: plus(totalFreezeEv, times(account.freezeBalanceEv, account.indexPriceEp)),
      };
    },
    {
      totalEquityEv: '0',
      totalEquityCanBorrowEv: '0',
      totalDebtEv: '0',
      totalDebtCanBorrowEv: '0',
      netEquityEv: '0',
      remainingBorrowTotalValueEv: '0',
      remainingBorrow2XTotalValueEv: '0',
      totalAvailableEv: '0',
      totalMarginEv: '0',
      totalFreezeEv: '0',
    }
  );
  const riskLevelEr = accounts[0].riskLevelEr;
  const maxTransferOutValueEv = calcMaxTransferOutValueEv(
    totalEquityCanBorrowEv,
    totalDebtCanBorrowEv,
    riskLevelEr,
    marginRiskLevels
  );

  const remainingBorrowTotalValueEv2 = max(remainingBorrowTotalValueEv, 0);
  const remainingBorrow2XTotalValueEv2 = max(remainingBorrow2XTotalValueEv, 0);

  return {
    overview: {
      netEquityEv,
      totalEquityEv,
      totalDebtEv,
      maxTransferOutValueEv,
      totalAvailableEv,
      totalMarginEv,
      totalFreezeEv,
    },
    accounts: accounts.map((account): MarginAccount => {
      const remainingBorrow = calcRemainingBorrow(
        account.canBorrow,
        remainingBorrowTotalValueEv2,
        account,
        marginRiskLevels
      );

      const remainingBorrow2X = calcRemainingBorrow(
        account.canBorrow,
        remainingBorrow2XTotalValueEv2,
        account,
        marginRiskLevels
      );
      const liquidationPrice = calcLiqPrice(
        account.canBorrow,
        totalEquityCanBorrowEv,
        totalDebtCanBorrowEv,
        account,
        marginRiskLevels
      );
      const maxTransferOutAmount = calcMaxTransferOutAmount(account.canBorrow, maxTransferOutValueEv, account);

      delete account.remainingBorrowValueEv;
      delete account.remainingBorrow2XValueEv;
      delete account.valuePrecision;
      delete account.pricePrecision;
      delete account.canBorrow;

      return {
        ...account,
        ...liquidationPrice,
        ...remainingBorrow,
        ...maxTransferOutAmount,
        remainingBorrow2XEv: remainingBorrow2X.remainingBorrowEv,
        remainingBorrow2X: remainingBorrow2X.remainingBorrow,
      };
    }),
  };
}

interface RemainingBorrow {
  remainingBorrowEv: string;
  remainingBorrow: string;
}

function calcRemainingBorrow(
  canBorrow,
  remainingBorrowTotalValueEv: string,
  account: InnerMarginAccount,
  marginRiskLevels: MarginRiskLevels
): RemainingBorrow {
  // 风险率小于等于 最小可借贷风险率 不可借贷
  if (!canBorrow || lte(account.riskLevelEr, marginRiskLevels.canBorrowAbove)) {
    return {
      remainingBorrowEv: '0',
      remainingBorrow: numUtil.floor(0, account.valuePrecision),
    };
  }
  /**
   * 剩余最大可借金额 remainingBorrowTotalEv = sum((TotalBalance - TotalDebt) * (MaxLeverage - 1) * coefficient - TotalDebt) * IndexPrice, ....)
   * 最大可借数量 remainingBorrowBalanceEv = remainingBorrowTotalEv / IndexPrice
   */
  const remainingBorrowEv = min(
    div(remainingBorrowTotalValueEv, account.indexPriceEp),
    account.remainingMaxAmountToBorrowEv
  );

  return {
    remainingBorrowEv,
    remainingBorrow: numUtil.delimit(numUtil.floor(remainingBorrowEv, account.valuePrecision)),
  };
}

interface LiqPrice {
  liquidationPriceEp: string;
  liquidationPrice: string;
}

/**
 * 强平价格 Lp = (Ao - Rs * Do) / (Rs * Pd - Pa)
 *
 * At: 包含当前仓位的总资产
 * Ao: 除当前仓位以外的总资产
 * Dt: 包含当前仓位的总负债
 * Do: 除当前仓位以外的总负债
 * Pa: 当前仓位总资产
 * Pd: 当前仓位总负债
 * Rs: 风险率
 * Lp: 强平价格
 *
 * Rs = (Ao + Pa * Lp) / (Do + Pd * Lp)
 * => Rs * (Do + Pd * Lp) = Ao + Pa * Lp
 * => Rs * Pd * Lp - Pa * Lp = Ao + - Rs * Do
 * => (Rs * Pd - Pa) * Lp = Ao + - Rs * Do
 * => Lp = (Ao - Rs * Do) / (Rs * Pd - Pa)
 *
 * => Lp = ((At - Pa * price) - Rs * (Dt - Pd * price)) / (Rs * Pd - Pa)
 * => Lp = (At - Rs * Dt) + (Rs * Pd * price - Pa * price) / (Rs * Pd - Pa)
 * => Lp = ((At - Rs * Dt) + (Rs * Pd - Pa) * price) / (Rs * Pd - Pa)
 * => Lp = (At - Rs * Dt) / (Rs * Pd - Pa) + price
 *
 * @param totalEquityEv 包含当前仓位的总资产
 * @param totalDebtEv 包含当前仓位的总负债
 * @param account 当前仓位数据
 * @returns
 */
function calcLiqPrice(
  canBorrow: boolean,
  totalEquityEv: string,
  totalDebtEv: string,
  account: InnerMarginAccount,
  marginRiskLevels: MarginRiskLevels
): LiqPrice {
  const calcLiqMaxRiskLevelEr = 10; // 风险率大于10, 不展示强平价格
  const defaultLiqPrice = {
    liquidationPriceEp: '0',
    liquidationPrice: '-',
  };

  // 不允许借贷
  // 当前风险率大于10, 不展示强平价格
  // 当前仓位 正资产&负资产 === 0
  if (
    !canBorrow ||
    gt(account.riskLevelEr, calcLiqMaxRiskLevelEr) ||
    (lte(account.totalBalanceEv, 0) && lte(account.totalDebtEv, 0))
  ) {
    return defaultLiqPrice;
  }

  const a = minus(totalEquityEv, times(totalDebtEv, marginRiskLevels.canTradeAbove));
  const b = minus(times(account.totalDebtEv, marginRiskLevels.canTradeAbove), account.totalBalanceEv);
  const liquidationPriceEp = toString(Big(a).div(b).plus(account.indexPriceEp));

  // 当前计算出来的 强平价≤ 0 不展示
  if (lte(liquidationPriceEp, 0)) {
    return defaultLiqPrice;
  }

  return {
    liquidationPriceEp,
    liquidationPrice: numUtil.delimit(toFixed(liquidationPriceEp, account.valuePrecision)),
  };
}

interface MaxTransferOutAmount {
  maxTransferOutAmountEv: string;
  maxTransferOutAmount: string;
}

function calcMaxTransferOutAmount(
  canBorrow,
  maxTransferOutValueEv: string,
  account: InnerMarginAccount
): MaxTransferOutAmount {
  if (!canBorrow) {
    const maxTransferOutAmountEv = minus(account.netEquityEv, account.freezeBalanceEv);
    return {
      maxTransferOutAmountEv: maxTransferOutAmountEv,
      maxTransferOutAmount: numUtil.delimit(numUtil.floor(maxTransferOutAmountEv, account.valuePrecision)),
    };
  }
  if (lte(account.availableBalanceEv, 0) || lte(maxTransferOutValueEv, 0)) {
    return getDefaultMaxTransferOutAmount(account.valuePrecision);
  }
  const maxTransferOutAmountEv = min(div(maxTransferOutValueEv, account.indexPriceEp), account.availableBalanceEv);
  return gt(maxTransferOutAmountEv, 0)
    ? {
        maxTransferOutAmountEv,
        maxTransferOutAmount: numUtil.delimit(numUtil.floor(maxTransferOutAmountEv, account.valuePrecision)),
      }
    : getDefaultMaxTransferOutAmount(account.valuePrecision);
}

function calcMaxTransferOutValueEv(totalEquityEv, totalDebtEv, riskLevelEr, marginRiskLevels): string {
  /**
   * 当 Risk Level > 最小可划转风险率 时，可划出资金至现货钱包或合约账户（可做抵押品资产）
   * 风险率 RiskLevel = (TotalAssetValue - TransferredOutValue) / (Liability + AccumulatedInterest)
   * 总负债 TotalDebt = Liability + AccumulatedInterest
   * 最小可划转风险率 minTransferRiskLevel
   * =>
   *    TransferredOutValue = TotalAssetValue - RiskLevel * TotalDebt
   *    maxTransferOutValue = TotalAssetValue - minTransferRiskLevel * TotalDebt
   *
   * TransferredOutValue = TransferredOutAmount * IndexPrice
   * =>
   *    TransferredOutAmount = (TotalAssetValue - RiskLevel * TotalDebt) / IndexPrice
   *    maxTransferOutAmount = min( (TotalAssetValue - minTransferRiskLevel * TotalDebt) / IndexPrice, Available )
   *
   * TotalAssetValue: 总资产
   * Liability: 总借贷
   * AccumulatedInterest: 总利息
   * TransferredOutValue: 转出金额
   * TransferredOutAmount: 转出数量
   * IndexPrice: 转出资产价格
   *
   * maxTransferOutAmount: 最大可转出数量
   */
  if (lte(riskLevelEr, marginRiskLevels.canTransferOutAbove)) {
    return '0';
  }

  return max(0, minus(totalEquityEv, times(totalDebtEv, marginRiskLevels.canTransferOutAbove)));
}

function getDefaultMaxTransferOutAmount(precision = 8): MaxTransferOutAmount {
  return {
    maxTransferOutAmountEv: '0',
    maxTransferOutAmount: (0).toFixed(precision),
  };
}

function convertBalanceToUsdEv(balanceEv, currency, indexPricesCache) {
  const currentIndexPrice = indexPricesCache.get('.' + currency);
  if (!currentIndexPrice) {
    return 0;
  }

  const balanceFactor = 1;
  const valueFactor = 1;
  return calcValueBalanceUtils.getValueEv(
    balanceEv,
    currentIndexPrice.priceEp,
    balanceFactor,
    currentIndexPrice.priceFactor,
    valueFactor
  );
}
